import { useEffect } from "react";

/*
 * Seo — a tiny, dependency-free per-route head manager.
 *
 * Why not react-helmet? This project deliberately avoids extra runtime deps.
 * A SPA only needs to keep the document <head> in sync with the active route,
 * which is a few imperative DOM upserts inside useEffect — no library required.
 *
 * Each page renders <Seo title=… description=… path=… /> once. We then
 * find-or-create the relevant tags and update them in place, so there are
 * never duplicates (the static baseline tags in index.html are reused).
 *
 * OPEN GRAPH IMAGE: og/twitter image points at `${SITE_ORIGIN}${OG_IMAGE}`.
 * Add the file at  513travel-main/public/og-513travel.jpg  (recommended size
 * 1200×630, < ~300 KB). If it is missing the build still succeeds — the tag is
 * just a URL string — but social previews won't show an image until you add it.
 */

// Canonical production origin (no trailing slash). DNS is not switched yet, but
// canonical/OG URLs should always advertise the final public domain.
export const SITE_ORIGIN = "https://513travel.com";
export const SITE_NAME = "513Travel";
export const OG_IMAGE = "/og-513travel.jpg"; // public/og-513travel.jpg (1200×630)
export const OG_IMAGE_WIDTH = "1200";
export const OG_IMAGE_HEIGHT = "630";
export const OG_IMAGE_ALT = "Golden sunrise over an African savanna landscape";

// Find an existing tag by selector or create it, append to <head>, return it.
function upsert(selector, create) {
  let el = document.head.querySelector(selector);
  if (!el) {
    el = create();
    document.head.appendChild(el);
  }
  return el;
}

function setMetaByName(name, content) {
  const el = upsert(`meta[name="${name}"]`, () => {
    const m = document.createElement("meta");
    m.setAttribute("name", name);
    return m;
  });
  el.setAttribute("content", content);
}

function setMetaByProperty(property, content) {
  const el = upsert(`meta[property="${property}"]`, () => {
    const m = document.createElement("meta");
    m.setAttribute("property", property);
    return m;
  });
  el.setAttribute("content", content);
}

function setCanonical(href) {
  const el = upsert('link[rel="canonical"]', () => {
    const l = document.createElement("link");
    l.setAttribute("rel", "canonical");
    return l;
  });
  el.setAttribute("href", href);
}

export default function Seo({ title, description, path = "/", type = "website", image }) {
  useEffect(() => {
    const url = SITE_ORIGIN + path;
    const ogImage = SITE_ORIGIN + (image || OG_IMAGE);

    document.title = title;
    setMetaByName("description", description);
    setCanonical(url);

    // Open Graph
    setMetaByProperty("og:site_name", SITE_NAME);
    setMetaByProperty("og:title", title);
    setMetaByProperty("og:description", description);
    setMetaByProperty("og:type", type);
    setMetaByProperty("og:url", url);
    setMetaByProperty("og:image", ogImage);
    setMetaByProperty("og:image:width", OG_IMAGE_WIDTH);
    setMetaByProperty("og:image:height", OG_IMAGE_HEIGHT);
    setMetaByProperty("og:image:alt", OG_IMAGE_ALT);

    // Twitter (no width/height equivalent exists; alt is the safe addition)
    setMetaByName("twitter:card", "summary_large_image");
    setMetaByName("twitter:title", title);
    setMetaByName("twitter:description", description);
    setMetaByName("twitter:image", ogImage);
    setMetaByName("twitter:image:alt", OG_IMAGE_ALT);
  }, [title, description, path, type, image]);

  return null;
}
